import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== DATA ====================

// Newest first. Each image `alt` carries what the flyer says, in its own language.
// `eyebrow` and an image `caption` are optional.
const announcements = [
  {
    id: 'save-for-college',
    title: 'NYC Scholarship Account',
    body: "If you haven't already activated your child's save for college account, please see this flyer for information on how to set it up or get support if needed. Kindergarten students will be able to activate their accounts in January.",
    images: [
      {
        src: '/assets/images/Save_for_College_EN.webp',
        width: 698,
        height: 942,
        alt: "NYC Scholarship Account flyer — Complete the steps below to activate your child's account. 1. Visit the website: nyckidsrise.org/activate, scroll down and click ACTIVATE ACCOUNT. 2. Enter the information: Student OSIS number, Student Date of Birth, Zip Code. 3. Create User Profile: enter your email and create a password! Questions: Reach out to Ms. Berkowitz at mberkowitz4@schools.nyc.gov.",
      },
    ],
  },
  {
    id: 'character-day-parade',
    title: 'Character Day Parade',
    body: 'Please come and see your little ones look great!',
    images: [
      {
        src: '/assets/images/Character_Day_Parade.webp',
        width: 920,
        height: 1194,
        alt: 'Character Day Parade flyer in English and Spanish (Desfile del Día de Personajes de Halloween) — October 30 / 30 de octubre. Please come and see your little ones look great! ¡Vengan a ver lo bien que se ven sus pequeños! Schedule / Horario: Pre-K — 8:15 AM; 2nd–5th — 9:00 AM; K–1st — 12:45 PM.',
      },
    ],
  },
  {
    id: 'wreath-making',
    eyebrow: 'Parent Workshop',
    title: 'Wreath Making',
    body: "Join us for a fun and creative parent workshop where you'll learn how to make your own beautiful holiday wreath!",
    images: [
      {
        src: '/assets/images/Wreath_Making_EN.webp',
        width: 770,
        height: 1158,
        alt: 'Parent Workshop for a Wreath Making flyer — Create, Connect, Celebrate. Join us for a fun and creative parent workshop where you’ll learn how to make your own beautiful holiday wreath! Date: November TBD. Location: PS 85 Great Expectations. All Parents Welcome! All supplies provided! We can’t wait to see you there!',
      },
    ],
  },
  {
    id: 'brag-tags',
    eyebrow: 'Attendance',
    title: 'Introducing Brag Tags!',
    body: 'PS 85 is celebrating excellent attendance with our brand new BRAG TAGS! Come in every day and get your brag tag!',
    images: [
      {
        src: '/assets/images/Brag_Tags_EN.webp',
        width: 972,
        height: 628,
        caption: 'English',
        alt: 'Introducing Brag Tags! Celebrating attendance and great expectations. Each student received one Brag Tag, the "Welcome to School" tag. Each month, students who attend school every day with outstanding attendance will receive a monthly tag. Pictured: the welcome tag and the monthly Outstanding Attendance tags.',
      },
      {
        src: '/assets/images/Brag_Tags_ES.webp',
        width: 976,
        height: 614,
        caption: 'Español',
        alt: '¡Presentamos las Etiquetas Brag Tags! Celebrando la asistencia y las grandes expectativas. Cada estudiante recibió una etiqueta de reconocimiento, la de bienvenida a la escuela. Cada mes, los estudiantes que asistan a clases todos los días con una asistencia sobresaliente recibirán una etiqueta mensual.',
        lang: 'es',
      },
    ],
  },
];

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const AnnouncementCard = ({ eyebrow, title, body, images }) => (
  <article className="p-6 md:p-10 bg-white rounded-3xl border border-gray-100 shadow-soft">
    <div className="max-w-3xl">
      {eyebrow && (
        <span className="inline-flex items-center gap-2 mb-3 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      )}
      <h2 className="text-3xl sm:text-4xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
        {title}
      </h2>
      <p className="mt-4 text-base md:text-lg text-gray-600 leading-7">{body}</p>
    </div>

    <div className="mt-8 grid md:grid-cols-2 gap-6">
      {images.map(({ src, width, height, caption, alt, lang }) => (
        <figure key={src}>
          <img
            src={src}
            width={width}
            height={height}
            alt={alt}
            lang={lang}
            loading="lazy"
            className="w-full h-auto rounded-2xl shadow-sm"
          />
          {caption && (
            <figcaption className="mt-2 text-sm font-semibold text-gray-600" lang={lang}>{caption}</figcaption>
          )}
        </figure>
      ))}
    </div>
  </article>
);

// ==================== MAIN COMPONENT ====================

const Announcements = () => (
  <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
    <Navbar />

    <main id="main-content">

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

        <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
          <div className="max-w-4xl bg-[#0F2240]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
              <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
              Parent Portal
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
              Announcements
            </h1>

            <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
              The latest news for PS 85 families
            </p>
          </div>
        </Container>
      </section>

      {/* ==================== ANNOUNCEMENTS ==================== */}
      <section className="py-20 md:py-28 bg-[#F5EFE0]">
        <Container className="space-y-10">
          {announcements.map(({ id, ...item }) => (
            <AnnouncementCard key={id} {...item} />
          ))}
        </Container>
      </section>

    </main>

    <Footer />
  </div>
);

export default Announcements;
